import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Bookmark,
  Check,
  ChevronDown,
  ChevronUp,
  Columns3,
  Plus,
  Search,
  Star,
  X,
} from "lucide-react";
import {
  branchMult,
  computeValue,
  stateFor,
} from "@/lib/utils/daylight-insight/compute";
import { fmtVal } from "@/lib/utils/daylight-insight/format";
import {
  DISCIPLINES,
  SHOW_BENCHMARK_LABEL,
} from "@/lib/utils/daylight-insight/constants";
import {
  CLIN_COLS,
  filterRoster,
  type ClinicianRow,
  type RosterFilters,
} from "@/lib/utils/daylight-insight/roster";
import type { DateRange, Metric } from "@/lib/utils/daylight-insight/types";
import { useStore } from "@/provider/store";
import { useSaveCustomView } from "@/components/daylight-insight/api/useSaveCustomView";
import { useCustomViews } from "@/components/daylight-insight/api/useCustomViews";
import { useDeleteCustomView } from "@/components/daylight-insight/api/useDeleteCustomView";
import type {
  CustomViewType,
  SavedCustomView,
} from "@/components/daylight-insight/api/types";
import { OptionSearch } from "./controls";

interface SortState {
  col: string;
  dir: "asc" | "desc";
}
interface SavedView {
  name: string;
  cols: string[];
  sort: SortState | null;
  locked?: boolean;
  isDefault?: boolean;
  /** `AppUserPerfID` for a server-side view; absent on the built-in ones, which
   *  is what makes them undeletable. */
  id?: number | string | null;
}

/** Side-effect hook for a saved view — persists it server-side. The return value
 *  is ignored (saving is fire-and-forget from the table's point of view), so any
 *  result type is accepted. */
type PersistViewFn = (
  name: string,
  payload: { columns: string[]; sort: SortState | null; isDefault: boolean },
) => unknown;

/** Side-effect hook for removing a saved view server-side, by its perf id. */
type DeleteViewFn = (id: number | string) => unknown;

/** The server-backed bits of {@link useTableControls}. Tables with no saved
 *  views (the seeded ones, All Metrics) pass none of it and keep the built-in
 *  view list. */
export interface TableControlsOptions {
  /** Persists a view — save, overwrite, or a change of default. */
  persistView?: PersistViewFn;
  /** Removes a view. Without it the ✕ only drops the view locally. */
  deleteView?: DeleteViewFn;
  /** The user's saved views. `null` = still loading, `[]` = none saved. */
  savedViews?: SavedView[] | null;
}

/** The always-present reset view. Not a saved view — it is what "show
 *  everything" means, and what the table falls back to when the user has no
 *  default saved. */
const ALL_VIEW = "All metrics";

/** Canonical form of a column id, for matching a SAVED view's columns against
 *  the ones a table has today. Case and separators are dropped, so
 *  "firstName" / "First Name" / "first_name" all agree. */
const normId = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Adapt the API's saved views to the picker's shape. `null` (still loading /
 *  not applicable) passes through so the table keeps its built-in views until
 *  the real list lands. */
export const toSavedViews = (
  views: SavedCustomView[] | null,
): SavedView[] | null =>
  views?.map((v) => ({
    id: v.id,
    name: v.name,
    cols: v.details.columns,
    sort: v.details.sort,
    isDefault: v.details.isDefault,
  })) ?? null;

export interface TableControls {
  metrics: Metric[];
  allIds: string[];
  views: SavedView[];
  visible: Set<string>;
  sort: SortState | null;
  active: string;
  visMetrics: Metric[];
  cycleSort: (id: string) => void;
  toggleCol: (id: string) => void;
  applyView: (v: SavedView) => void;
  saveView: (name: string) => void;
  setDefault: (name: string) => void;
  delView: (name: string) => void;
  showAll: () => void;
  clearAll: () => void;
}

/**
 * Shared column-manager / sort / saved-views state for any metric table.
 *
 * `savedViews` are the user's own views as returned by
 * `dashboard/custom-views`. Pass `undefined` (the default) for tables with no
 * server-side views — they keep the built-in "All metrics" + "Core N" pair.
 * Pass an array (empty is meaningful: "you have none saved") and the picker
 * shows exactly those views plus the locked "All metrics" reset.
 *
 * Whichever saved view carries `isDefault` is selected and applied on arrival,
 * with no user interaction. If none does, the table shows every column.
 */
export function useTableControls(
  metrics: Metric[],
  coreCount = 5,
  opts: TableControlsOptions = {},
): TableControls {
  const { persistView, deleteView, savedViews } = opts;
  const allIds = metrics.map((m) => m.id);

  // The view list for a given (metrics, savedViews) pair. Saved columns are
  // intersected with the columns that actually exist now, so a view saved before
  // a metric was dropped still applies cleanly instead of showing nothing.
  const buildViews = useCallback(
    (ids: string[], saved?: SavedView[] | null): SavedView[] => {
      const reset: SavedView = { name: ALL_VIEW, cols: ids, sort: null, locked: true };
      if (!saved) {
        const v: SavedView[] = [{ ...reset, isDefault: true }];
        if (ids.length > coreCount)
          v.push({ name: `Core ${coreCount}`, cols: ids.slice(0, coreCount), sort: null });
        return v;
      }
      // Saved columns are matched to the columns that exist NOW, ignoring case
      // and separators: a view stores column IDS, and those drift. The
      // By-Clinician table's ids are the SP's own aliases, so a view saved as
      // "firstName" has to still find today's "First Name"; the By-Branch ids
      // are MetricCodes, which the SP itself spells inconsistently
      // ("ReferralCNT" vs "ReferralCnt"). First id wins, so the SP's own order
      // decides any collision.
      const byNorm = new Map<string, string>();
      for (const id of ids) {
        const k = normId(id);
        if (!byNorm.has(k)) byNorm.set(k, id);
      }
      const mine = saved.map((v) => {
        const cols = Array.from(
          new Set(
            v.cols.map((c) => byNorm.get(normId(c))).filter((c): c is string => !!c),
          ),
        );
        // A view that resolves to NOTHING was saved against a column set this
        // table no longer has. Applying it would blank the table ("0 of N"), so
        // fall back to every column — and re-saving it then stores real ids.
        return { ...v, cols: cols.length ? cols : ids };
      });
      // No saved default → the reset view is the default, i.e. show everything.
      const hasDefault = mine.some((v) => v.isDefault && v.cols.length);
      return [{ ...reset, isDefault: !hasDefault }, ...mine];
    },
    [coreCount],
  );

  const seed = useMemo<SavedView[]>(
    () => buildViews(allIds, savedViews),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metrics, savedViews, buildViews],
  );
  const [views, setViews] = useState<SavedView[]>(seed);
  const def = views.find((v) => v.isDefault) || views[0];
  const [visible, setVisible] = useState<Set<string>>(() => new Set(def.cols));
  const [sort, setSort] = useState<SortState | null>(def.sort);
  const [active, setActive] = useState(def.name);

  // The state above is seeded once via useState initializers. Re-seed when the
  // metric set changes or the saved views land AFTER mount (both arrive async),
  // so the default columns actually populate instead of staying empty ("0 of N")
  // and the user's saved default is applied without them touching anything.
  // Keyed on the metric ids + the saved-view signature, so a refetch that
  // changes neither leaves the user's column/sort choices intact.
  const idKey = allIds.join("|");
  // "not loaded" must NOT collide with "loaded, but you have none saved" — the
  // latter still has to re-seed, to drop the built-in views the picker showed
  // while the request was in flight.
  const savedKey =
    savedViews == null
      ? "~"
      : savedViews.map((v) => `${v.name}:${v.isDefault ? 1 : 0}:${v.cols.join(",")}`).join("|");
  const seedKey = `${idKey}#${savedKey}`;
  const seededKey = useRef(seedKey);
  // The saved default is auto-applied ONCE, when the list first arrives. Later
  // re-seeds (a refetch after saving or starring) must not yank the user out of
  // the view they are looking at.
  const appliedDefault = useRef(false);
  useEffect(() => {
    if (seededKey.current === seedKey) return;
    seededKey.current = seedKey;
    const nextViews = buildViews(allIds, savedViews);
    setViews(nextViews);

    const firstLoad = !appliedDefault.current && savedViews != null;
    if (firstLoad) appliedDefault.current = true;
    // Re-apply the SAME view the user is on (rebuilt against the new metric
    // set), so a late-arriving metric list repopulates its columns instead of
    // leaving the table empty. Falls back to the default when that view is gone
    // — and on first load the saved default always wins.
    const target =
      (!firstLoad && nextViews.find((v) => v.name === active)) ||
      nextViews.find((v) => v.isDefault) ||
      nextViews[0];
    setVisible(new Set(target.cols));
    setSort(target.sort);
    setActive(target.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, buildViews]);

  const visMetrics = metrics.filter((m) => visible.has(m.id));
  const cycleSort = (id: string) =>
    setSort((p) =>
      !p || p.col !== id
        ? { col: id, dir: "desc" }
        : p.dir === "desc"
          ? { col: id, dir: "asc" }
          : null,
    );
  const toggleCol = (id: string) => {
    setVisible((v) => {
      const n = new Set(v);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
    setActive("Custom");
  };
  const applyView = (v: SavedView) => {
    setVisible(new Set(v.cols));
    setSort(v.sort);
    setActive(v.name);
  };
  const saveView = (name: string) => {
    const nm = (name || "").trim() || `View ${views.length}`;
    const cols = metrics.filter((m) => visible.has(m.id)).map((m) => m.id);
    // A NEW view is never the default — starring it is a separate, explicit act.
    // Saving over an existing name is an update, so its flag carries over rather
    // than being silently cleared.
    const isDefault = !!views.find((v) => v.name === nm && !v.locked)?.isDefault;
    setViews([...views.filter((v) => v.name !== nm), { name: nm, cols, sort, isDefault }]);
    setActive(nm);
    if (persistView) void persistView(nm, { columns: cols, sort, isDefault });
  };
  // Starring re-saves the views whose flag actually moved, so the choice
  // survives a reload. The locked reset view is UI-only and never persisted;
  // starring it just clears the saved default (everything shows next time).
  //
  // Starring also SELECTS the view: marking one default means "this is the one I
  // want", so its columns and sort apply straight away rather than only on the
  // next load.
  const setDefault = (name: string) => {
    const next = views.map((v) => ({ ...v, isDefault: v.name === name }));
    const starred = next.find((v) => v.name === name);
    if (starred) applyView(starred);
    setViews(next);
    if (persistView) {
      next.forEach((v, i) => {
        if (v.locked || !!v.isDefault === !!views[i].isDefault) return;
        void persistView(v.name, { columns: v.cols, sort: v.sort, isDefault: !!v.isDefault });
      });
    }
  };
  // Optimistic: the view leaves the picker at once and the server catches up.
  // A failed delete puts it back rather than leaving the UI lying — the next
  // load would resurrect it anyway. Built-in views carry no id and can't be
  // deleted (the ✕ isn't rendered for them).
  const delView = (name: string) => {
    const gone = views.find((v) => v.name === name);
    const left = views.filter((v) => v.name !== name);
    setViews(left);
    // Deleting the view you are looking at drops you back to the default (or the
    // reset view) rather than leaving its columns up under a name that's gone.
    if (active === name && left.length)
      applyView(left.find((v) => v.isDefault) || left[0]);
    if (!deleteView || gone?.id == null) return;
    void Promise.resolve(deleteView(gone.id)).then((ok) => {
      if (ok === false)
        setViews((cur) => (cur.some((v) => v.name === name) ? cur : [...cur, gone]));
    });
  };
  const showAll = () => {
    setVisible(new Set(allIds));
    setActive(ALL_VIEW);
  };
  // The mirror of "Show all": empties the column set so the user can tick the
  // handful they want instead of untick the dozens they don't. It lands on no
  // saved view, so the picker reads "Custom" like any other manual toggle.
  const clearAll = () => {
    setVisible(new Set());
    setActive("Custom");
  };
  return {
    metrics,
    allIds,
    views,
    visible,
    sort,
    active,
    visMetrics,
    cycleSort,
    toggleCol,
    applyView,
    saveView,
    setDefault,
    delView,
    showAll,
    clearAll,
  };
}

interface TableToolbarProps {
  ctl?: TableControls;
  title: ReactNode;
  children?: ReactNode;
  query?: string;
  setQuery?: (v: string) => void;
  searchPlaceholder?: string;
}
export function TableToolbar({
  ctl,
  title,
  children,
  query,
  setQuery,
  searchPlaceholder,
}: TableToolbarProps) {
  const [colOpen, setColOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [colQuery, setColQuery] = useState("");
  const colRef = useRef<HTMLDivElement>(null),
    viewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (colRef.current && !colRef.current.contains(e.target as Node))
        setColOpen(false);
      if (viewRef.current && !viewRef.current.contains(e.target as Node))
        setViewOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  // Closing the column picker drops its search, so it reopens on the full list.
  useEffect(() => {
    if (!colOpen) setColQuery("");
  }, [colOpen]);
  const save = () => {
    ctl?.saveView(newName);
    setNewName("");
  };
  const colQ = colQuery.trim().toLowerCase();
  const shownCols = ctl
    ? colQ
      ? ctl.metrics.filter((m) => m.name.toLowerCase().includes(colQ))
      : ctl.metrics
    : [];
  return (
    <div className="tbl-toolbar">
      <span className="pp-section-lbl" style={{ margin: 0 }}>
        {title}
        {ctl && (
          <span className="tbl-count">
            {" "}
            · {ctl.visMetrics.length} of {ctl.metrics.length} metrics
          </span>
        )}
      </span>
      {setQuery && (
        <div className="tbl-search">
          <Search size={14} />
          <input
            placeholder={searchPlaceholder || "Filter…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button onClick={() => setQuery("")} aria-label="clear">
              <X size={13} />
            </button>
          )}
        </div>
      )}
      <div style={{ flex: 1 }} />
      {children}
      {ctl && (
        <div style={{ position: "relative" }} ref={viewRef}>
          <button className="tbl-ctrl" onClick={() => setViewOpen(!viewOpen)}>
            <Bookmark size={13} />
            {ctl.active}
            <ChevronDown size={12} />
          </button>
          {viewOpen && (
            <div className="dl-pop tbl-pop">
              {ctl.views.map((v) => (
                <div
                  key={v.name}
                  className={"view-opt" + (v.name === ctl.active ? " on" : "")}
                >
                  <button
                    className="view-apply"
                    onClick={() => {
                      ctl.applyView(v);
                      setViewOpen(false);
                    }}
                  >
                    {v.name}
                    {v.isDefault && (
                      <span className="view-deftag">default</span>
                    )}
                  </button>
                  <button
                    className="view-star"
                    title="Set as default"
                    onClick={() => ctl.setDefault(v.name)}
                  >
                    <Star
                      size={12}
                      fill={v.isDefault ? "currentColor" : "none"}
                    />
                  </button>
                  {!v.locked && (
                    <button
                      className="view-del"
                      title="Delete view"
                      onClick={() => ctl.delView(v.name)}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
              <div className="view-save">
                <input
                  placeholder="Save current view as…"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && save()}
                />
                <button onClick={save} title="Save">
                  <Plus size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {ctl && (
        <div style={{ position: "relative" }} ref={colRef}>
          <button className="tbl-ctrl" onClick={() => setColOpen(!colOpen)}>
            <Columns3 size={13} />
            Columns
            <ChevronDown size={12} />
          </button>
          {colOpen && (
            <div className="dl-pop tbl-pop">
              <OptionSearch
                query={colQuery}
                setQuery={setColQuery}
                placeholder="Search columns…"
              />
              <div className="col-pop-head">
                <span>
                  {ctl.visMetrics.length} of {ctl.metrics.length} shown
                </span>
                <span className="col-pop-acts">
                  <button onClick={ctl.showAll}>Show all</button>
                  <button className="col-pop-clear" onClick={ctl.clearAll}>
                    Clear all
                  </button>
                </span>
              </div>
              {shownCols.map((m) => (
                <div
                  key={m.id}
                  className="dl-opt"
                  onClick={() => ctl.toggleCol(m.id)}
                >
                  <span
                    className={"dl-box" + (ctl.visible.has(m.id) ? " on" : "")}
                  >
                    {ctl.visible.has(m.id) && <Check size={11} />}
                  </span>
                  <span style={{ flex: 1 }}>{m.name}</span>
                </div>
              ))}
              {shownCols.length === 0 && (
                <div className="dl-noopt">
                  No columns match {colQuery ? colQuery.trim() : null}.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SortHead({ ctl, m }: { ctl: TableControls; m: Metric }) {
  return (
    <th
      className="num bb-col sortable"
      onClick={() => ctl.cycleSort(m.id)}
      title={"Sort by " + m.name}
    >
      <span className="th-inner">
        {m.name}
        {ctl.sort &&
          ctl.sort.col === m.id &&
          (ctl.sort.dir === "desc" ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronUp size={12} />
          ))}
      </span>
    </th>
  );
}

export function BranchBreakdown({
  metrics,
  range,
  branchFilter,
}: {
  metrics: Metric[];
  range: DateRange;
  branchFilter: string[];
}) {
  // The branch universe is the user's live, SP-scoped list off the store.
  const all = useStore().accessList;
  const brs =
    branchFilter && branchFilter.length
      ? all.filter((b) => branchFilter.includes(b))
      : all;
  const ctl = useTableControls(metrics);
  const [query, setQuery] = useState("");
  const valOf = (b: string, m: Metric) =>
    computeValue(m, range) * branchMult(b, m.id);
  const filtered = brs.filter((b) =>
    b.toLowerCase().includes(query.toLowerCase()),
  );
  const sortMetric = ctl.sort
    ? metrics.find((m) => m.id === ctl.sort!.col)
    : null;
  const rows = sortMetric
    ? [...filtered].sort((a, b) => {
        const d = valOf(a, sortMetric) - valOf(b, sortMetric);
        return ctl.sort!.dir === "asc" ? d : -d;
      })
    : filtered;
  return (
    <div className="pp-card" style={{ padding: 0, overflow: "visible" }}>
      <TableToolbar
        ctl={ctl}
        title="By Branch"
        query={query}
        setQuery={setQuery}
        searchPlaceholder="Filter branches…"
      />
      <div style={{ overflowX: "auto" }}>
        <table className="clintbl">
          <thead>
            <tr>
              <th>Branch</th>
              {ctl.visMetrics.map((m) => (
                <SortHead key={m.id} ctl={ctl} m={m} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b}>
                <td className="who">{b}</td>
                {ctl.visMetrics.map((m) => {
                  const v = valOf(b, m);
                  const st = stateFor(m, v);
                  return (
                    <td key={m.id} className={"num " + st}>
                      {fmtVal(m, v)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={ctl.visMetrics.length + 1}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-3)",
                    padding: 22,
                  }}
                >
                  No branches match "{query}".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A branch row for the API-driven table: a stable id (BranchSystemID) plus the
 *  display name. */
export interface ApiBranchRow {
  id: string;
  name: string;
}

/**
 * By-Branch table backed by live API data (POST dashboard/dashboard-metric →
 * MetricBranch). Same look and controls as {@link BranchBreakdown}, but rows,
 * columns and cell values all come from the server instead of the seed layer.
 * `valueOf(rowId, metricId)` returns the current per-branch value (null → "—").
 */
export function BranchBreakdownApi({
  metrics,
  rows: allRows,
  valueOf,
  loading,
  error,
  exportSheet,
  title = "By Branch",
  dashboardID,
  customViewType,
  showViewControls = true,
}: {
  metrics: Metric[];
  rows: ApiBranchRow[];
  valueOf: (rowId: string, metricId: string) => number | null;
  loading?: boolean;
  error?: string | null;
  /** Worksheet name to publish this table under for the header's Export button.
   *  Opt-in: screens that export from their own toolbar (All Metrics) omit it. */
  exportSheet?: string;
  /** Toolbar heading. The dashboard spec's table `title` when it sets one. */
  title?: string;
  /** Operating View: the active dashboard id sent to `@DashboardID`. */
  dashboardID?: number | string | null;
  /** When set, saving a custom view calls POST /dashboard/save-custom-view. */
  customViewType?: CustomViewType;
  /** Shows/hides the "All metrics" and "Columns" dropdowns. */
  showViewControls?: boolean;
}) {
  // The user's saved views for this table. On a dashboard these come free with
  // the page's own `custom-views` call (see `DashboardPerfsProvider`) — the
  // table fires no request of its own — and are re-read after a save so a new
  // view appears without a page refresh. A null `dashboardID` is a real scope,
  // not a missing one: All Metrics isn't tied to a dashboard, and its views are
  // stored (and read back) under `@DashboardID = NULL`; having no page-level
  // response to read, it fetches here.
  const { views: savedViews, refetch: refetchViews } = useCustomViews(
    customViewType ? { dashboardID: dashboardID ?? null } : null,
    customViewType ?? "branch",
  );
  const { saveCustomView } = useSaveCustomView();
  const persistView = customViewType
    ? (
        name: string,
        payload: { columns: string[]; sort: SortState | null; isDefault: boolean },
      ) =>
        saveCustomView(
          name,
          {
            viewType: customViewType,
            columns: payload.columns,
            sort: payload.sort,
            isDefault: payload.isDefault,
          },
          dashboardID,
        ).then((ok) => {
          if (ok) refetchViews();
          return ok;
        })
    : undefined;
  const { deleteCustomView } = useDeleteCustomView();
  const removeView = customViewType
    ? (id: number | string) =>
        deleteCustomView(id).then((ok) => {
          if (ok) refetchViews();
          return ok;
        })
    : undefined;
  const ctl = useTableControls(metrics, 5, {
    persistView,
    deleteView: removeView,
    savedViews: customViewType ? toSavedViews(savedViews) : undefined,
  });
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? allRows.filter((b) => b.name.toLowerCase().includes(q))
    : allRows;
  const sortMetric = ctl.sort
    ? metrics.find((m) => m.id === ctl.sort!.col)
    : null;
  const rows = sortMetric
    ? [...filtered].sort((a, b) => {
        // Nulls sink to the bottom regardless of direction.
        const va = valueOf(a.id, sortMetric.id);
        const vb = valueOf(b.id, sortMetric.id);
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return ctl.sort!.dir === "asc" ? va - vb : vb - va;
      })
    : filtered;

  // Publish the grid exactly as displayed — visible columns, current sort, active
  // search — so Export writes what the user is looking at. Deliberately no dep
  // array: `rows`/`visMetrics` are rebuilt every render, and re-registering only
  // overwrites a ref (see `registerDetailTable`), so there is nothing to loop on.
  const { registerDetailTable } = useStore();
  const exportId = useId();
  useEffect(() => {
    if (!exportSheet) return;
    registerDetailTable(exportId, () => ({
      title: exportSheet,
      header: ["Branch", ...ctl.visMetrics.map((m) => m.name)],
      rows: rows.map((b) => [
        b.name,
        ...ctl.visMetrics.map((m) => {
          const v = valueOf(b.id, m.id);
          return v === null ? "—" : fmtVal(m, v);
        }),
      ]),
    }));
    return () => registerDetailTable(exportId, null);
  });

  return (
    <div className="pp-card" style={{ padding: 0, overflow: "visible" }}>
      <TableToolbar
        ctl={showViewControls ? ctl : undefined}
        title={title}
        query={query}
        setQuery={setQuery}
        searchPlaceholder="Filter branches…"
      />
      <div style={{ overflowX: "auto" }}>
        <table className="clintbl">
          <thead>
            <tr>
              <th>Branch</th>
              {ctl.visMetrics.map((m) => (
                <SortHead key={m.id} ctl={ctl} m={m} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <td className="who">{b.name}</td>
                {ctl.visMetrics.map((m) => {
                  const v = valueOf(b.id, m.id);
                  if (v === null)
                    return (
                      <td
                        key={m.id}
                        className="num"
                        style={{ color: "var(--ink-3)" }}
                      >
                        —
                      </td>
                    );
                  const st = stateFor(m, v);
                  return (
                    <td key={m.id} className={"num " + st}>
                      {fmtVal(m, v)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={ctl.visMetrics.length + 1}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-3)",
                    padding: 22,
                  }}
                >
                  {loading
                    ? "Loading branch data…"
                    : error
                      ? error
                      : q
                        ? `No branches match "${query}".`
                        : "No branch data."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ClinicianTable({
  roster,
  filters,
  exportSheet,
}: {
  roster: ClinicianRow[];
  filters: RosterFilters;
  /** Worksheet name to publish this table under for the header's Export button. */
  exportSheet?: string;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortState>({ col: "attain", dir: "desc" });
  const cycleSort = (key: string) =>
    setSort((p) =>
      !p || p.col !== key
        ? { col: key, dir: "desc" }
        : p.dir === "desc"
          ? { col: key, dir: "asc" }
          : { col: "attain", dir: "desc" },
    );
  const rows = useMemo(() => {
    let r = filterRoster(roster, filters);
    const q = query.trim().toLowerCase();
    if (q)
      r = r.filter(
        (x) =>
          x.name.toLowerCase().includes(q) ||
          x.disc.toLowerCase().includes(q) ||
          x.job.toLowerCase().includes(q),
      );
    const col = CLIN_COLS.find((c) => c.key === sort.col)!;
    return [...r].sort((a, b) => {
      const va = col.get(a),
        vb = col.get(b);
      const d =
        typeof va === "number"
          ? va - (vb as number)
          : String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? d : -d;
    });
  }, [roster, filters, query, sort]);

  // Same contract as BranchBreakdownApi: publish the rows as displayed for the
  // header's Export button. See the note there on the missing dep array.
  const { registerDetailTable } = useStore();
  const exportId = useId();
  useEffect(() => {
    if (!exportSheet) return;
    registerDetailTable(exportId, () => ({
      title: exportSheet,
      header: CLIN_COLS.map((c) => c.label),
      rows: rows.map((r) =>
        CLIN_COLS.map((c) => {
          const v = c.get(r);
          return c.fmt ? c.fmt(v as number) : v;
        }),
      ),
    }));
    return () => registerDetailTable(exportId, null);
  });

  return (
    <div className="pp-card" style={{ padding: 0, overflow: "visible" }}>
      <TableToolbar
        title={
          <>
            By Clinician
            <span className="tbl-count"> · {rows.length} clinicians</span>
          </>
        }
        query={query}
        setQuery={setQuery}
        searchPlaceholder="Find a clinician…"
      />
      <div style={{ overflowX: "auto", maxHeight: 540 }}>
        <table className="clintbl">
          <thead>
            <tr>
              {CLIN_COLS.map((c) => (
                <th
                  key={c.key}
                  className={(c.num ? "num " : "") + "sortable"}
                  onClick={() => cycleSort(c.key)}
                  title={"Sort by " + c.label}
                >
                  {/* Numeric headers align right with their cells (see .clintbl). */}
                  <span className="th-inner">
                    {c.label}
                    {sort.col === c.key &&
                      (sort.dir === "desc" ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronUp size={12} />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                {CLIN_COLS.map((c) => {
                  const v = c.get(r);
                  const cls =
                    (c.num ? "num " : "") +
                    (c.who ? "who " : "") +
                    (c.state ? c.state(r) : "");
                  return (
                    <td key={c.key} className={cls.trim()}>
                      {c.tag ? (
                        <span className="disc-tag">{v}</span>
                      ) : c.fmt ? (
                        c.fmt(v as number)
                      ) : (
                        v
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={CLIN_COLS.length}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-3)",
                    padding: 24,
                  }}
                >
                  No clinicians match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BarsByDiscipline({
  roster,
  filters,
}: {
  roster: ClinicianRow[];
  filters: RosterFilters;
}) {
  const data = useMemo(() => {
    const rows = filterRoster(roster, filters),
      tally: Record<string, number[]> = {};
    rows.forEach((r) => (tally[r.disc] = tally[r.disc] || []).push(r.attain));
    return DISCIPLINES.filter((d) => tally[d]).map((d) => ({
      d,
      v: tally[d].reduce((a, b) => a + b, 0) / tally[d].length,
    }));
  }, [roster, filters]);
  const max = Math.max(...data.map((x) => x.v), 100);
  return (
    <div className="pp-card">
      <div className="pp-section-lbl" style={{ margin: "0 0 14px" }}>
        Avg Attainment by Discipline
      </div>
      {data.map((x) => {
        const good = x.v >= 98;
        return (
          <div
            key={x.d}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                width: 36,
                fontSize: 11,
                fontWeight: 600,
                color: "var(--ink-2)",
              }}
            >
              {x.d}
            </span>
            <div
              style={{
                flex: 1,
                height: 18,
                background: "var(--surface-3)",
                borderRadius: 5,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: (x.v / max) * 100 + "%",
                  height: "100%",
                  background: good ? "var(--green)" : "var(--chart-primary)",
                  borderRadius: 5,
                }}
              />
            </div>
            <span
              className="num"
              style={{
                width: 50,
                textAlign: "right",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {x.v.toFixed(1)}%
            </span>
          </div>
        );
      })}
      {data.length === 0 && (
        <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
          No data for current filters.
        </div>
      )}
      {SHOW_BENCHMARK_LABEL && (
        <div
          style={{
            fontSize: 11,
            color: "var(--ink-3)",
            marginTop: 8,
            borderTop: "1px solid var(--line-soft)",
            paddingTop: 10,
          }}
        >
          Benchmark 98% · bars at or above target show green.
        </div>
      )}
    </div>
  );
}
